import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Rainbow, Users, Sparkles, Compass } from "lucide-react";
import ExplorePrefsDialog from "@/components/ExplorePrefsDialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * SidePickerModal — mandatory first-login modal that asks the viewer to
 * pick the audience side they want to see (LGBTQ or Straight). Feeds, live
 * streams, communities, and recommendations are filtered by this choice.
 *
 * Mounted globally by AppShell. Opens whenever the current user has no
 * `side` set. It CANNOT be dismissed until a side is picked — that's what
 * "mandatory" means. Users can switch later from Profile Edit.
 */
export default function SidePickerModal({ open, forceLocked = true, defaultSide = null, onSaved }) {
  const { refresh } = useAuth();
  const [choice, setChoice] = useState(defaultSide);
  const [busy, setBusy] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const pick = async () => {
    if (!choice || busy) return;
    setBusy(true);
    try {
      await api.post("/users/me/side", { side: choice });
      await refresh();
      toast.success(
        choice === "lgbtq" ? "Welcome to the LGBTQ side of Panda X 🌈"
          : choice === "explore" ? "Explore Your Way — you'll see both sides of Panda X."
          : "Welcome to the Straight side of Panda X."
      );
      if (choice === "explore") setPrefsOpen(true);
      onSaved && onSaved(choice);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your choice.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={() => { /* locked when forceLocked */ }}>
      <DialogContent
        data-testid="side-picker-modal"
        onEscapeKeyDown={(e) => forceLocked && e.preventDefault()}
        onPointerDownOutside={(e) => forceLocked && e.preventDefault()}
        onInteractOutside={(e) => forceLocked && e.preventDefault()}
        className={`max-w-md w-full bg-black border border-white/10 rounded-none p-0 overflow-hidden ${forceLocked ? "[&>button.absolute]:hidden" : ""}`}
      >
        <div className="p-8 pb-6">
          <DialogTitle asChild>
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-rose-500 mb-3">
                <Sparkles size={11} /> Pick your side
              </div>
              <h2 className="font-display text-3xl leading-tight">
                Which side of My Panda X{" "}
                <span className="text-rose-400">is yours?</span>
              </h2>
            </div>
          </DialogTitle>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            Pick the LGBTQ or Straight side to keep your feed, live streams and
            communities inside one audience — or choose Explore Your Way to see
            both and filter by who you want to meet. Switch anytime from the side pill.
          </p>
        </div>

        <div className="px-8 space-y-3">
          <button
            type="button"
            onClick={() => setChoice("lgbtq")}
            data-testid="side-picker-lgbtq"
            className={`w-full text-left border-2 transition-all p-5 ${
              choice === "lgbtq"
                ? "border-rose-500 bg-gradient-to-br from-rose-950/60 via-black to-black"
                : "border-white/10 hover:border-white/20 bg-white/[0.02]"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center ${choice === "lgbtq" ? "text-rose-400" : "text-zinc-400"}`}>
                  <Rainbow size={26} />
                </div>
                <div>
                  <div className="font-display text-xl">LGBTQ Side</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Queer creators, communities, and audiences.
                  </div>
                </div>
              </div>
              {choice === "lgbtq" && (
                <div className="text-[10px] uppercase tracking-widest text-rose-400">Selected</div>
              )}
            </div>
          </button>

          <button
            type="button"
            onClick={() => setChoice("straight")}
            data-testid="side-picker-straight"
            className={`w-full text-left border-2 transition-all p-5 ${
              choice === "straight"
                ? "border-rose-500 bg-gradient-to-br from-rose-950/60 via-black to-black"
                : "border-white/10 hover:border-white/20 bg-white/[0.02]"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center ${choice === "straight" ? "text-rose-400" : "text-zinc-400"}`}>
                  <Users size={26} />
                </div>
                <div>
                  <div className="font-display text-xl">Straight Side</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Straight creators, communities, and audiences.
                  </div>
                </div>
              </div>
              {choice === "straight" && (
                <div className="text-[10px] uppercase tracking-widest text-rose-400">Selected</div>
              )}
            </div>
          </button>

          <button
            type="button"
            onClick={() => setChoice("explore")}
            data-testid="side-picker-explore"
            className={`w-full text-left border-2 transition-all p-5 ${
              choice === "explore" ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-white/30"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center ${choice === "explore" ? "text-amber-300" : "text-zinc-400"}`}>
                  <Compass size={26} />
                </div>
                <div>
                  <div className="font-display text-xl">Explore Your Way</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    See both sides. Filter by your gender and who you want to meet — men, women, multiple genders, or everyone.
                  </div>
                </div>
              </div>
              {choice === "explore" && (
                <div className="text-[10px] uppercase tracking-widest text-amber-300">Selected</div>
              )}
            </div>
          </button>
        </div>

        <div className="p-8 pt-6">
          <Button
            type="button"
            onClick={pick}
            disabled={!choice || busy}
            data-testid="side-picker-confirm"
            className="btn-crimson rounded-none w-full py-6 text-sm font-medium disabled:opacity-40"
          >
            {busy ? "Saving…" : choice === "explore" ? "Continue — Explore Your Way" : choice ? `Continue as ${choice === "lgbtq" ? "LGBTQ" : "Straight"} side` : "Pick a side to continue"}
          </Button>
          <p className="text-[10px] text-zinc-600 text-center mt-3 leading-relaxed">
            You can switch sides anytime from your profile settings.
          </p>
        </div>
      </DialogContent>
    </Dialog>
    <ExplorePrefsDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
    </>
  );
}
